import React, { useRef, useState } from "react";
import { toPng, toBlob } from "html-to-image";
import { X, Download, Copy, Check, Loader2, Share2 } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { api, formatValue } from "@/lib/api";
import { toast } from "sonner";

const FMT_LABEL = { "1qb": "1QB", superflex: "SUPERFLEX", tep: "TE PREMIUM" };

export default function TradeVerdictCard({ result, format = "1qb", onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linking, setLinking] = useState(false);

  if (!result) return null;

  const winner =
    result.winner === "side_a" ? "SIDE A WINS" :
    result.winner === "side_b" ? "SIDE B WINS" : "EVEN TRADE";
  const winnerColor = result.winner === "even" ? "#00E5FF" : "#CCFF00";
  const fairColor = result.fairness >= 90 ? "#CCFF00" : result.fairness >= 75 ? "#F59E0B" : "#FF3366";

  const render = async (fn) => {
    if (!cardRef.current) return null;
    return fn(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#050505" });
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = await render(toPng);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dynasty-metrics-verdict.png`;
      a.click();
      toast.success("Verdict card downloaded");
    } catch (e) {
      toast.error("Could not generate image");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    setBusy(true);
    try {
      const blob = await render(toBlob);
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      toast.success("Copied — paste into your league chat");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy not supported here — use Download");
    } finally { setBusy(false); }
  };

  const shareLink = async () => {
    setLinking(true);
    try {
      const r = await api.post("/trade/share", { result: { ...result, format } });
      await navigator.clipboard.writeText(r.data.url);
      toast.success("Public link copied — anyone can view this verdict");
    } catch {
      toast.error("Couldn't create a share link. Use Download/Copy instead.");
    } finally { setLinking(false); }
  };

  const Side = ({ title, side, isWinner }) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.2em", color: "#888", marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 22, fontWeight: 700, color: isWinner ? "#CCFF00" : "#fff", marginBottom: 10 }}>
        {formatValue(side.value)}
      </div>
      {side.players.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, padding: "4px 0", borderTop: "1px solid #1a1a1a", color: "#ccc" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {p.name} <span style={{ color: "#555" }}>{p.position}</span>
          </span>
          <span style={{ fontFamily: "JetBrains Mono, monospace", color: "#888" }}>{formatValue(p.value)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
      data-testid="verdict-modal"
      onClick={onClose}
    >
      <div className="my-auto" onClick={(e) => e.stopPropagation()}>
        {/* The shareable card */}
        <div
          ref={cardRef}
          data-testid="verdict-card"
          style={{
            width: 400, background: "#050505",
            backgroundImage:
              "radial-gradient(circle at 70% 0%, rgba(204,255,0,0.10), transparent 55%), linear-gradient(rgba(204,255,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(204,255,0,0.04) 1px, transparent 1px)",
            backgroundSize: "auto, 36px 36px, 36px 36px",
            border: "1px solid #222", borderRadius: 4, padding: 28,
            color: "#fff", position: "relative", overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BrandMark size={26} />
              <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 900, fontSize: 15, letterSpacing: "-0.02em" }}>Dynasty Metrics</div>
            </div>
            <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 9, letterSpacing: "0.18em", color: "#666", border: "1px solid #222", padding: "3px 7px", borderRadius: 3 }}>
              {FMT_LABEL[format] || format.toUpperCase()}
            </div>
          </div>

          <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.25em", color: "#666", marginBottom: 4 }}>// AI TRADE VERDICT</div>
          <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 900, fontSize: 40, lineHeight: 1, letterSpacing: "-0.03em", color: winnerColor, textTransform: "uppercase", marginBottom: 18 }}>
            {winner}
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 22 }}>
            <div>
              <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 9, letterSpacing: "0.2em", color: "#666" }}>FAIRNESS</div>
              <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 46, fontWeight: 700, lineHeight: 1, color: fairColor }}>{result.fairness}%</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 9, letterSpacing: "0.2em", color: "#666" }}>VALUE DIFF <span style={{ border: "1px solid rgba(0,255,65,0.45)", background: "rgba(0,255,65,0.12)", color: "#00FF41", fontSize: 8, fontWeight: 700, padding: "1px 4px", borderRadius: 2, marginLeft: 4, letterSpacing: "0.12em" }}>DME</span></div>
              <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 20, fontWeight: 700, color: "#fff" }}>
                {result.diff >= 0 ? "+" : ""}{formatValue(result.diff)}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 18 }}>
            <Side title="SIDE A GIVES" side={result.side_a} isWinner={result.winner === "side_b"} />
            <div style={{ width: 1, background: "#1a1a1a" }} />
            <Side title="SIDE B GIVES" side={result.side_b} isWinner={result.winner === "side_a"} />
          </div>

          <div style={{ marginTop: 22, paddingTop: 12, borderTop: "1px solid #1a1a1a", display: "flex", justifyContent: "space-between", fontFamily: "JetBrains Mono, monospace", fontSize: 9, letterSpacing: "0.15em", color: "#555" }}>
            <span>DYNASTYMETRICS.COM</span>
            <span>4-SOURCE DYNASTY VALUES</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between gap-2 mt-4" style={{ width: 400 }}>
          <button onClick={onClose} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="verdict-close">
            <X className="w-4 h-4" /> Close
          </button>
          <div className="flex gap-2">
            <button onClick={shareLink} disabled={linking} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="verdict-link">
              {linking ? <Loader2 className="w-4 h-4 spin" /> : <Share2 className="w-4 h-4" />} Link
            </button>
            <button onClick={copy} disabled={busy} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="verdict-copy">
              {copied ? <Check className="w-4 h-4 text-[var(--accent)]" /> : <Copy className="w-4 h-4" />} Copy
            </button>
            <button onClick={download} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="verdict-download">
              {busy ? <Loader2 className="w-4 h-4 spin" /> : <Download className="w-4 h-4" />} Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
